import { fold } from './fold.ts'
import type { Concert } from './program-schema.ts'

/**
 * Reading a schedule someone built in Broadcast (brdca.st) and finding the same
 * concerts in our own programme.
 *
 * Broadcast is the festival's own companion app, so its schedules are the one place a
 * user is likely to have already picked their concerts by the time they open this app.
 * Importing one turns that half hour of work into thumbs-up marks here instead of
 * asking for it a second time.
 *
 * Matching is by artist name only — deliberately, even though the page prints a time
 * and a stage beside every entry. Broadcast renders its start times in UTC, so the
 * page shows Mogwai at 15:25 where the official programme says 17:25; matching on a
 * time that is reliably two hours out would fail on every row, and matching on a time
 * we first had to shift by a guessed offset would fail silently the year the offset
 * changes. Names are unique across the 2026 programme, and our own snapshot already
 * carries the authoritative time and stage for whatever a name resolves to.
 */

/**
 * The rendered artist name of one schedule item.
 *
 * `bcfw-cl-schedule-item-name` is a semantic hook class Broadcast puts on the element
 * itself, sitting among the utility classes its stylesheet actually uses — so it is
 * the one thing on the page that is there to be found rather than to be styled.
 *
 * Matched against the class attribute rather than the element, and terminated by the
 * first `</div>`: an item whose name were ever wrapped in an inner element would yield
 * a truncated name here. That degrades to the name being reported as not found, which
 * the caller shows the user, rather than to a wrong concert being marked.
 */
const ITEM_NAME = /class="[^"]*bcfw-cl-schedule-item-name[\s"][^>]*>([\s\S]*?)<\/div>/g

const TAGS = /<[^>]*>/g

/**
 * The origin tag Broadcast appends to a name: `Mogwai (SCT)`, `TOMORA (UK/NO)`.
 *
 * Two or three letters, optionally several separated by slashes, and only at the very
 * end — tight enough that it cannot eat a parenthesis that is part of an artist's
 * actual name. The match is tried with the tag intact first anyway (see
 * `matchBroadcastNames`), so a band that genuinely calls itself `(BR)` still wins.
 */
const ORIGIN_TAG = /\s*\(\s*[A-Za-z]{2,3}(?:\s*\/\s*[A-Za-z]{2,3})*\s*\)$/

const ENTITY = /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
}

function fromCodePoint(value: number, whole: string): string {
  // An out-of-range escape is left as written rather than thrown on: it can only
  // reach a name we then report as not found, which is recoverable, whereas throwing
  // would fail the whole import over one malformed character.
  return Number.isInteger(value) && value > 0 && value <= 0x10ffff
    ? String.fromCodePoint(value)
    : whole
}

/**
 * Decodes the HTML escapes that can appear in a name.
 *
 * The page is rendered by React, whose escaper emits exactly `&amp;`, `&lt;`, `&gt;`,
 * `&quot;` and `&#x27;` and leaves every other character as literal UTF-8 — so this
 * is the complete set for this input, not a hopeful subset. It matters: two of the
 * forty entries in a 2026 schedule carry one (`Melody&#x27;s Echo Chamber`,
 * `Nick Cave &amp; The Bad Seeds`), and both would otherwise go unmatched.
 *
 * One pass over the string, so `&amp;lt;` decodes to the literal `&lt;` it encoded
 * rather than being decoded twice into `<`.
 */
export function decodeEntities(value: string): string {
  return value.replace(ENTITY, (whole, body: string) => {
    const key = body.toLowerCase()
    if (key.startsWith('#x')) return fromCodePoint(Number.parseInt(key.slice(2), 16), whole)
    if (key.startsWith('#')) return fromCodePoint(Number.parseInt(key.slice(1), 10), whole)
    return NAMED_ENTITIES[key] ?? whole
  })
}

/** Artist names in a Broadcast schedule page, in the order the page lists them. */
export function parseBroadcastNames(html: string): string[] {
  const names: string[] = []
  for (const match of html.matchAll(ITEM_NAME)) {
    const name = decodeEntities((match[1] ?? '').replace(TAGS, '')).trim()
    if (name !== '') names.push(name)
  }
  return names
}

export interface BroadcastImport {
  /** Concerts in the pasted schedule that exist in the programme, deduplicated. */
  matched: Concert[]
  /** Names in the pasted schedule with no counterpart in the programme. */
  unmatched: string[]
}

/**
 * Resolves parsed names against the programme.
 *
 * Everything the caller needs to be honest about the result comes back: what will be
 * marked, and what was in the schedule but is not in our programme. Broadcast covers
 * Øyanatt and the club programme, which this app's snapshot deliberately leaves out,
 * so a schedule containing entries we cannot place is normal rather than a fault —
 * and telling the user which ones is the difference between "40 marked" and a silent
 * partial import.
 */
export function matchBroadcastNames(
  concerts: readonly Concert[],
  names: readonly string[],
): BroadcastImport {
  const byName = new Map<string, Concert>()
  for (const concert of concerts) {
    const key = fold(concert.name)
    // First wins. Two concerts folding to one key would be an ambiguity in the
    // programme itself, and resolving it by programme order at least keeps this
    // deterministic instead of dependent on iteration order changing upstream.
    if (!byName.has(key)) byName.set(key, concert)
  }

  const matched: Concert[] = []
  const unmatched: string[] = []
  const already = new Set<string>()

  for (const name of names) {
    const concert = byName.get(fold(name)) ?? byName.get(fold(name.replace(ORIGIN_TAG, '')))
    if (!concert) {
      unmatched.push(name)
      continue
    }
    if (already.has(concert.id)) continue
    already.add(concert.id)
    matched.push(concert)
  }

  return { matched, unmatched }
}
